import React, { useEffect, useState } from "react";
import DialogBox from "../DialogBox";
import { locale } from "../../utils/LocaleManager";
import { useContentStore } from "../../stores/contentStore";
import TreeView from "../TreeView";
import {
  Box,
  Card,
  CardContent,
  Container,
  Grid,
  Stack,
  Alert,
  Button,
  Typography,
} from "@mui/material";
import { moveVideos } from "../../models/video";
import { moveVideoToFolder } from "../../services/folderServices";
import { getThumbnailByVideoID } from "../../services/contentServices";
import { Bounce, toast } from "react-toastify";
import { useFolderStore } from "../../stores/folderStore";
import { useRolePermissionsStore } from "../../stores/rolePermissionsStore";
import { handleError } from "../../utils/utilFunctions";
import { NodeModel } from "@minoru/react-dnd-treeview";

const MoveVideo = () => {
  const {
    setIsShowMoveDialog,
    selectedThumbnails,
    setSelectedThumbnails,
    setIsLoading,
  } = useContentStore((state: any) => ({
    setIsShowMoveDialog: state.setIsShowMoveDialog,
    selectedThumbnails: state.selectedThumbnails,
    setSelectedThumbnails: state.setSelectedThumbnails,
    setIsLoading: state.setIsLoading,
  }));

  const { setIsMoveSuccess } = useFolderStore((state: any) => ({
    setIsMoveSuccess: state.setIsMoveSuccess,
  }));

  const rolePermissions = useRolePermissionsStore(
    (state: any) => state.rolePermissions,
  );

  const [destinationFolderId, setDestinationFolderId] = useState<NodeModel["id"] | null>(null);
  const [destinationFolderName, setDestinationFolderName] = useState<string>("");
  const [inlineError, setInlineError] = useState<string>("");
  const [previewUrls, setPreviewUrls] = useState<Record<string, string>>({});

  useEffect(() => {
    let cancelled = false;
    const ownedUrls: string[] = [];
    const revokeOwnedUrls = () => {
      if (typeof URL.revokeObjectURL === "function") {
        ownedUrls.forEach((url) => URL.revokeObjectURL(url));
      }
    };

    const loadPreviews = async () => {
      const entries = await Promise.all(
        selectedThumbnails.map(async (thumbnail: any) => {
          const blob = await getThumbnailByVideoID(thumbnail.videoIndexId);

          if (
            !(blob instanceof Blob) ||
            typeof URL.createObjectURL !== "function"
          ) {
            return null;
          }

          const url = URL.createObjectURL(blob);
          ownedUrls.push(url);

          return [thumbnail.videoIndexId, url] as const;
        }),
      );

      if (cancelled) {
        revokeOwnedUrls();
        return;
      }

      setPreviewUrls(
        entries.reduce<Record<string, string>>((urls, entry) => {
          if (entry) {
            urls[entry[0]] = entry[1];
          }

          return urls;
        }, {}),
      );
    };

    loadPreviews();

    return () => {
      cancelled = true;
      revokeOwnedUrls();
    };
  }, [selectedThumbnails]);

  const fetchMoveVideo = async (id: string | null, request: moveVideos) => {
    try {
      setIsLoading(true);
      setInlineError("");
      const response = await moveVideoToFolder(id, request);
      if (response?.status === 200) {
        setIsMoveSuccess(true);
        setIsShowMoveDialog(false);
        setSelectedThumbnails([]);
        toast.success(
          selectedThumbnails.length === 1
            ? `Move "${selectedThumbnails[0].name}" to "${destinationFolderName}"`
            : `${selectedThumbnails.length} videos have been moved to "${destinationFolderName}"`,
          {
            position: "bottom-left",
            autoClose: 2000,
            hideProgressBar: false,
            closeOnClick: false,
            pauseOnHover: false,
            pauseOnFocusLoss: false,
            draggable: true,
            progress: undefined,
            theme: "colored",
            transition: Bounce,
            onClose: async () => {
              setIsMoveSuccess(false);
            },
          },
        );
      } else {
        const status = response?.response?.status;
        handleError({
          status,
          toast,
          errorMessage: "",
          onClose: () => {},
        });
        // 403: close dialog, clear selection, and preserve destination context
        if (status === 403) {
          setIsShowMoveDialog(false);
          setSelectedThumbnails([]);
        } else if (status !== 401) {
          // Non-401, non-403: show inline error with retry
          setInlineError(`Failed to move videos. Please try again.`);
        }
      }
    } catch (error) {
      console.error("Error moving videos:", error);
      handleError({
        status: 0,
        toast,
        errorMessage: `Error moving videos: ${error}`,
        onClose: () => {},
      });
      // Show inline error with retry for non-HTTP failures
      setInlineError(`Failed to move videos. Please try again.`);
    } finally {
      setIsLoading(false);
    }
  };

  const moveVideos = async () => {
    if (!rolePermissions?.video?.move || destinationFolderId === null) return;
    if (destinationFolderId === 1 && !rolePermissions?.folder?.root) return;
    if (typeof destinationFolderId !== "string" && destinationFolderId !== 1) return;

    const requestBody = {
      videoIndexIds: selectedThumbnails.map((thumb: any) => thumb.videoIndexId),
    };
    const id = destinationFolderId === 1 ? null : destinationFolderId;
    await fetchMoveVideo(id, requestBody);
  };

  const handleRetry = async () => {
    setInlineError("");
    await moveVideos();
  };

  // Capability-based: Manager (no folder.root) cannot select root; no destination = disabled
  const disableSubmit =
    !rolePermissions?.video?.move ||
    destinationFolderId === null ||
    (destinationFolderId === 1 && !rolePermissions?.folder?.root);

  const renderMoveDialog = () => (
    <Container>
      {inlineError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={handleRetry}>
              Retry
            </Button>
          }
        >
          {inlineError}
        </Alert>
      )}
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Box
            sx={{
              width: "100%",
              height: "25rem",
              border: "0.06rem solid #8A8E94",
              borderRadius: "0.5rem",
              overflow: "auto",
            }}
          >
            <Stack>
              {selectedThumbnails.map((thumbnail: any) => (
                <Card variant="outlined" key={thumbnail.videoIndexId}>
                  <CardContent style={{ padding: "1.25rem" }}>
                    <Stack direction="row" gap="0.625rem">
                      <img
                        src={previewUrls[thumbnail.videoIndexId] || ""}
                        style={{
                          height: "auto",
                          width: "2.5rem",
                          borderRadius: "0.25rem",
                          alignSelf: "stretch",
                        }}
                        alt={thumbnail.name}
                      />
                      {thumbnail.name}
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box
            sx={{
              width: "100%",
              height: "25rem",
              border: "0.06rem solid #8A8E94",
              borderRadius: "0.5rem",
              overflow: "auto",
              padding: "0.5rem",
            }}
          >
            <Typography
              data-testid="destination-instruction"
              tabIndex={-1}
              role="status"
              sx={{
                fontWeight: 600,
                fontSize: "0.875rem",
                mb: 1,
                outline: 'none',
              }}
            >
              {locale.destination_instruction}
            </Typography>
            <TreeView
              autoFocus
              onMoveDestinationSelected={(node: NodeModel) => {
                setDestinationFolderId(node.id);
                setDestinationFolderName(node.text);
              }}
            />
          </Box>
        </Grid>
      </Grid>
    </Container>
  );

  return (
    <DialogBox
      header={
        selectedThumbnails.length === 1
          ? `Move "${selectedThumbnails[0].name}" to "${destinationFolderName || "..."}"`
          : `Move ${selectedThumbnails.length} videos to "${destinationFolderName || "..."}"`
      }
      width="1200px"
      body={<></>}
      subBody={renderMoveDialog()}
      onCancel={() => setIsShowMoveDialog(false)}
      onSubmit={moveVideos}
      isDelete={false}
      primaryButton={locale.control_move}
      secondaryButton={locale.cancel}
      disablePrimary={disableSubmit}
    />
  );
};

export default MoveVideo;
